import { RpcError } from '@protobuf-ts/runtime-rpc'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { C1Link } from 'components/core/links/C1Link'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export type RestartTaskDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const RestartTaskDialogV2: FC<RestartTaskDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<void> => {
        try {
            await ticketActionTracker?.restart(task)
        } catch (err: unknown) {
            return toast(
                <FormattedMessage
                    id="task_restart_error"
                    defaultMessage="Error restarting task {task_link}. {code}: {msg}"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }

        toast(
            <FormattedMessage
                id="task_restart_success"
                defaultMessage="Task {task_link} restarted successfully"
                values={{
                    task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                }}
            />,
            'success',
        )

        close()
    }, [close, task, ticketActionTracker, toast])

    return (
        <SimpleConfirmationDialog
            submitActionLabel={intl.formatMessage({
                id: 'restart_confirmation_dialog_action_string',
                defaultMessage: 'Restart',
            })}
            title={intl.formatMessage({
                id: 'restart_confirmation_dialog_title',
                defaultMessage: 'Are you sure you want to restart this task?',
            })}
            details={intl.formatMessage({
                id: 'restart_confirmation_dialog_description',
                defaultMessage:
                    'Restarting a task will restore it to its original state. Any changes, decisions, or actions a user has previously committed will be reset.',
            })}
            onConfirm={handleClick}
            onCancel={close}
            inProgress={!ticketActionTracker}
            close={close}
        />
    )
}
